// stores/driverStore.js
'use client';
import { fleetApi } from '../api/axiosClient';
import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { useMemo } from 'react';
import { normalizeApiResponse } from '../utils/response';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;
const getCompanyId = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('companyId');
      if (stored && String(stored).toLowerCase() !== 'all') {
        const n = Number(stored);
        if (Number.isFinite(n)) return n;
      }
    }
  } catch (err) {
    console.warn('getCompanyId: cannot read localStorage', err);
  }
  return 0;
};
const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateDriverList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/driver/search');
};

export const useDriverList = (page, rowsPerPage, selectedCompanies = [], searchKey, searchValue) => {
  const companies = Array.isArray(selectedCompanies)
    ? selectedCompanies.map((c) => c?.value ?? c).join(',')
    : typeof selectedCompanies === 'string' && selectedCompanies !== 'all'
      ? selectedCompanies
      : '';

  const start = page * rowsPerPage;
  const payload = useMemo(() => {
    const builder = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .like(searchKey, searchValue, 'AND')
      .addJoin('company', ['name'])
      .addJoin('vehicle', ['license_plate'])
      .addJoin('driver_license', ['license_name'])
      .setOrder(['name'], true);
    if (companies) {
      builder.in('company_id', companies);
    }

    return builder.build();
  }, [start, rowsPerPage, companies, searchKey, searchValue]);
  const key = useMemo(() => ['/driver/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  const result = normalizeApiResponse(data, error, isLoading);
  return result;
};

export const getDriverById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));
    const payload = fb.build();
    const response = await fleetApi.post(`/driver/search${getTzParam()}`, payload);

    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    const result = Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
    return result;
  } catch (error) {
    console.error('Failed to fetch driver by ID:', error);
    throw error;
  }
};

export const createDriver = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.post(`/driver/new${getTzParam()}`, body);

    if (response?.data?.error === true || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateDriverList();
    return response.data;
  } catch (error) {
    console.error('Error creating driver:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateDriver = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      company: formData.company ?? getCompanyId(),
      ...formData
    };

    const response = await fleetApi.put(`/driver/edit/${id}${getTzParam()}`, body);

    if (response?.data?.error === true || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateDriverList();
    return response.data;
  } catch (error) {
    console.error('Error updating driver:', error?.response?.data ?? error);
    throw error;
  }
};

export const deleteDriver = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/driver/delete/${id}${getTzParam()}`);

    if (response?.data?.error === true || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateDriverList();
    return response.data;
  } catch (error) {
    console.error('Error deleting driver:', error?.response?.data ?? error);
    throw error;
  }
};

export const getDriverLicenseImage = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.get(`/driver/license/images/${id}${getTzParam()}`, {
      responseType: 'arraybuffer'
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching driver license image:', error?.response?.data ?? error);
    throw error;
  }
};
